import { differenceInCalendarDays, isToday, isYesterday } from "date-fns";

/**
 * Chat history reads as a timeline, not a table: a row's date only matters
 * relative to now. "Aug 12, 2026" on every row is noise when half of them are
 * from today — so rows carry a short relative stamp and the list is bucketed.
 */
/** `fmt` is `useLocale().formatDate` — weekday and month names must come from
 *  the active locale, so this module cannot call bare `format()`. */
export const dayLabel = (
  date: string | Date,
  t: Translator,
  fmt: DateFormatter,
): string => {
  const value = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(value.getTime())) return "";
  if (isToday(value)) return fmt(value, "HH:mm");
  if (isYesterday(value)) return t("my-space:ui.yesterday");
  const age = differenceInCalendarDays(new Date(), value);
  if (age < 7) return fmt(value, "EEEE");
  if (value.getFullYear() === new Date().getFullYear())
    return fmt(value, "MMM d");
  return fmt(value, "MMM d, yyyy");
};

const bucketOf = (date: Date, t: Translator, fmt: DateFormatter): string => {
  if (isToday(date)) return t("my-space:ui.today");
  if (isYesterday(date)) return t("my-space:ui.yesterday");
  const age = differenceInCalendarDays(new Date(), date);
  if (age < 7) return t("my-space:ui.previous_7_days");
  if (age < 30) return t("my-space:ui.previous_30_days");
  return fmt(date, "MMMM yyyy");
};

/** Translator supplied by the calling component — this module is not one. */
type Translator = (key: string) => string;
/** `useLocale().formatDate`, for the same reason. */
type DateFormatter = (date: Date, pattern: string) => string;

export interface IConversationGroup<T> {
  label: string;
  items: T[];
}

/**
 * Buckets by recency, pinned first. Order inside a bucket is left as the API
 * returned it — the server already sorts, and re-sorting here would fight
 * pagination.
 */
export const groupByRecency = <T>(
  items: T[],
  getDate: (item: T) => string | Date,
  t: Translator,
  fmt: DateFormatter,
  isPinned?: (item: T) => boolean,
): IConversationGroup<T>[] => {
  const pinnedLabel = t("my-space:ui.pinned_group");
  const groups: IConversationGroup<T>[] = [];
  const indexOf = new Map<string, number>();

  const push = (label: string, item: T) => {
    const at = indexOf.get(label);
    if (at === undefined) {
      indexOf.set(label, groups.length);
      groups.push({ label, items: [item] });
      return;
    }
    groups[at].items.push(item);
  };

  items.forEach((item) => {
    if (isPinned?.(item)) return push(pinnedLabel, item);
    const date = new Date(
      typeof getDate(item) === "string"
        ? (getDate(item) as string)
        : (getDate(item) as Date),
    );
    push(
      Number.isNaN(date.getTime())
        ? t("my-space:ui.earlier")
        : bucketOf(date, t, fmt),
      item,
    );
  });

  // "Pinned" is a status, not a date — it always leads.
  return groups.sort((a, b) =>
    a.label === pinnedLabel ? -1 : b.label === pinnedLabel ? 1 : 0,
  );
};
